import * as React from 'react';

import { GradientBackground } from '../../atoms/GradientBackground';
import { cn } from '../../lib/utils';

export interface OnboardingLayoutProps {
  /** Left column (logo + label + progress stepper). */
  left: React.ReactNode;
  /** Right column (the welcome card). */
  right: React.ReactNode;
  /**
   * Merged onto the glass wrapper (the two-column row). Lets a caller set the
   * frame's own geometry — the wizard uses it for Figma `2776:8539`'s fixed
   * 1000px width, 190px column gap and stretched columns.
   */
  contentClassName?: string;
  className?: string;
}

/**
 * OnboardingLayout — full-screen gradient background + a centered glass card
 * with `left` and `right` slots (Figma `5:2624` + `5:2507`). Reproduces
 * `AuthLayout`'s glass-wrapper tokens rather than editing it, isolating
 * onboarding from auth (design D1). The card is genuinely two-column, so the
 * slots are named rather than a single `children` (design D7).
 */
export const OnboardingLayout = React.forwardRef<HTMLDivElement, OnboardingLayoutProps>(
  ({ left, right, contentClassName, className }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('min-h-screen w-full relative overflow-hidden font-outfit', className)}
      >
        <GradientBackground />
        {/* w-max: an absolutely-positioned flex row has its shrink-to-fit width
            capped by the space from `left-1/2` to the viewport edge, which
            squished the fixed-size card. max-content sizes to the real 1057px. */}
        <div
          className={cn(
            'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-max bg-dawn-white/60 border-2 border-solid border-dawn-white rounded-lg p-10 flex gap-10 items-start',
            contentClassName
          )}
        >
          {/* self-stretch, not h-full: `height: 100%` against the auto-height
              glass row resolved to `auto` and also cancelled the row's stretch,
              so a left column could never match the card's height. */}
          <div className="flex flex-col gap-10 self-stretch items-start shrink-0">{left}</div>
          {right}
        </div>
      </div>
    );
  }
);

OnboardingLayout.displayName = 'OnboardingLayout';
